import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../Authcontext';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../../../api';

const AssistantManagerRatings = () => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [noDataPage, setNoDataPage] = useState(1);
  const [data, setData] = useState([]);
  const [noDataUsers, setNoDataUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const itemsPerPage = 10;

  const { accessToken } = useAuth();
  const navigate = useNavigate();

  const headers = [
    'S.no', 'Client', 'Division', 'End Client', 'Scope', 'Emp ID', 'Emp Name',
    'Department', 'Designation', 'Target', 'Achieved', 'Production %', 'Rating Category', 'Actions'
  ];

  const fetchData = useCallback(async () => {
    if (!selectedMonth) return;

    setLoading(true);
    setError(null);

    const year = selectedMonth.getFullYear();
    const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
    const formattedMonth = `${year}-${month}`;

    try {
      const baseUrls = getRoundRobinBaseUrl();
      const response = await api.post(`${baseUrls}/pqa/getAssiggManagerMonthlyEmployee`, {
        month: formattedMonth
      }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (response.data?.success) {
        setData(Array.isArray(response.data.data) ? response.data.data : []);
        setNoDataUsers(Array.isArray(response.data.noDataUsers) ? response.data.noDataUsers : []);
      } else {
        setData([]);
        setNoDataUsers([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load data');
      setData([]);
      setNoDataUsers([]);
    } finally {
      setLoading(false);
      setCurrentPage(1);
      setNoDataPage(1);
    }
  }, [selectedMonth, accessToken]);

  useEffect(() => {
    const now = new Date();
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setSelectedMonth(previousMonth);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportBoth = () => {
    const formattedMonth = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;
    const ratingData = data
      .filter(row => row.productivity <= 100)
      .map((row, index) => ({
        'S.no': index + 1,
        // 'Location': row.location,
        'Client': row.client,
        'Division': row.division,
        'End Client': row.endClient,
        'Scope': row.scope,
        'Emp ID': row.empId,
        'Emp Name': row.empName,
        'Department': row.department,
        'Designation': row.designation,
        'Target': String(row.totalTarget).slice(0, 4),
        'Achieved': row.totalAchieved,
        'Production %': row.productivity,
        'Rating Category': row.ratingCategory,
      }));

    const noDataSheet = noDataUsers.map((row, index) => ({
      'S.no': index + 1,
      'Emp ID': row.empId,
      'Emp Name': row.empName,
      'DOJ': row.dateOfJoining,
      'Department': row.department,
      'Designation': row.designation,
      // 'Location': row.location,
      'Status': row.status,
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(ratingData), 'Ratings');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(noDataSheet), 'NoDataUsers');
    XLSX.writeFile(workbook, `Rating_Report_With_NoData_${formattedMonth}.xlsx`);

    Swal.fire({
      title: 'Excel file downloaded successfully.',
      icon: 'success',
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 3000,
    });
  };

  const handleApprove = async (row) => {
    if (!selectedMonth) return;

    const baseUrls = getRoundRobinBaseUrl();
    const month = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;
    const body = {
      employeeId: row.empId,
      month: month,
      scopeId: row.scopeId || '',
      empName: row.empName,
      percentage: row.productivity
    };

    try {
      const response = await api.post(`${baseUrls}/pbi/createPbiInitiationApprove`, body, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (response.data?.success) {
        Swal.fire({
          title: `Approval successful for ${row.empName} (${row.empId})`,
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
        });

        navigate('/ScopeMgmt/PBI/AddEditPBI');
      } else {
        Swal.fire({
          title: `Approval failed for ${row.empName} (${row.empId})`,
          icon: 'error',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error('Approve Error:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.message,
        icon: 'error',
        toast: true,
        position: 'top',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const filteredData = data.filter(row => row.productivity <= 100);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = filteredData.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);

  const monthPicker = (
    <>
      <label className="text-lg font-medium">Rating Month:</label>
      <DatePicker
        selected={selectedMonth}
        onChange={(date) => {
          const now = new Date();
          if (date?.getMonth() === now.getMonth() && date?.getFullYear() === now.getFullYear()) {
            Swal.fire({
              title: 'Warning',
              text: 'Current month is still in progress.',
              icon: 'warning',
              toast: true,
              position: 'top',
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }
          setSelectedMonth(date);
        }}
        dateFormat="MM/yyyy"
        showMonthYearPicker
        className="form-input w-40 border rounded px-2 py-1"
      />
      <button
        onClick={() => setSelectedMonth(null)}
        className="bg-red-500 hover:bg-red-600 text-white font-semibold px-3 py-2 rounded"
      >
        Clear
      </button>
    </>
  );

  return (
    <div className="p-6 space-y-10">
      <div className="bg-white shadow-2xl rounded-xl border border-gray-200 p-6">
        <h1 className="text-3xl text-center font-bold text-[#1A2B5C] mb-4">Rating Report</h1>

        <div className="p-4 mb-4 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] shadow-sm flex flex-wrap gap-4 items-center">
          {monthPicker}
          <button
            onClick={handleExportBoth}
            disabled={!selectedMonth || (data.length === 0 && noDataUsers.length === 0)}
            className={`${selectedMonth && (data.length > 0 || noDataUsers.length > 0)
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-gray-400 cursor-not-allowed'
              } text-white font-semibold px-4 py-2 rounded`}
          >
            Export Excel
          </button>
        </div>

        {loading ? (
          <div className="text-center text-lg text-gray-500">Loading data...</div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto border border-[#E5E7EB] rounded-lg shadow-sm">
              <table className="min-w-full table-auto">
                <thead className="bg-[#1A2B5C] text-white text-sm">
                  <tr>
                    {headers.map((header, i) => (
                      <th key={i} className="px-4 py-2 border border-gray-200 whitespace-nowrap">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan={headers.length} className="text-center py-4 text-gray-500">
                        No data available
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((row, i) => (
                      <tr key={i} className="text-sm text-center">
                        <td className="px-4 py-2 border">{indexOfLastItem - itemsPerPage + i + 1}</td>
                        {/* <td className="px-4 py-2 border">{row.location}</td> */}
                        <td className="px-4 py-2 border whitespace-nowrap">{row.client}</td>
                        <td className="px-4 py-2 border whitespace-nowrap">{row.division}</td>
                        <td className="px-4 py-2 border">{row.endClient}</td>
                        <td className="px-4 py-2 border whitespace-nowrap">{row.scope}</td>
                        <td className="px-4 py-2 border">{row.empId}</td>
                        <td className="px-4 py-2 border whitespace-nowrap">{row.empName}</td>
                        <td className="px-4 py-2 border">{row.department}</td>
                        <td className="px-4 py-2 border whitespace-nowrap">{row.designation}</td>
                        <td className="px-4 py-2 border">{row.totalTarget}</td>
                        <td className="px-4 py-2 border">{row.totalAchieved}</td>
                        <td className="px-4 py-2 border">{row.productivity}</td>
                        <td className="px-4 py-2 border"> {row.ratingCategory}</td>
                        <td className="px-4 py-2 border">
                          <button
                            className="bg-blue-600 hover:bg-green-500 text-white px-3 py-1 rounded text-sm font-medium"
                            onClick={() => handleApprove(row)}
                          >
                            Approve
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-center mt-4 gap-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded-md text-sm font-medium border ${currentPage === i + 1 ? 'bg-[#1A2B5C] text-white' : 'bg-white text-[#1A2B5C]'}`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssistantManagerRatings;
